import type { Usuario } from './tipos'

/**
 * La visita del equipo a la cuenta de un cliente (CLAUDE.md §4, «Entrar en la cuenta de un
 * cliente»): un superadmin abre sesión **como el propietario** durante una hora. La sesión es la
 * del propietario —su correo, sus espacios—, pero no es suya: cuando caduca, pedir «la contraseña
 * de» ese correo no tiene sentido, porque el equipo no la tiene ni debe tenerla. Y no se alarga:
 * el núcleo no renueva una sesión de visita (`impedirRenovacionEnSuplantacion`).
 *
 * El panel lo pregunta al núcleo (`GET /api/suplantacion/estado`) mientras la sesión está viva,
 * y así sabe, cuando se va, que lo que ha terminado es una visita y no una sesión cualquiera.
 * La lógica va aparte de React para poder probarla.
 */
export type EstadoDeVisita = { activa: boolean; usuario?: string; cliente?: number; expira?: number }

export type Visita = {
  /** El cliente en cuya cuenta se ha entrado, y su nombre para decírselo al equipo. */
  cliente: number
  nombre: string
  /** El correo del propietario con el que se entró. */
  email: string
  /** Cuándo termina, en segundos desde la época (lo que lleva el token). */
  expira: number
}

/**
 * La visita en curso, si la sesión lo es. El nombre del cliente sale de los espacios del propio
 * usuario (el propietario, con `?depth=1`); si no está, se dice con el correo.
 */
export const visitaDe = (estado: EstadoDeVisita | null | undefined, usuario: null | Usuario): null | Visita => {
  if (!estado?.activa || typeof estado.cliente !== 'number' || typeof estado.expira !== 'number') return null
  const espacio = (usuario?.clientes ?? []).map((fila) => fila.cliente).find((c) => typeof c === 'object' && c !== null && c.id === estado.cliente)
  const email = estado.usuario || usuario?.email || ''
  const nombre = typeof espacio === 'object' && espacio?.nombre ? espacio.nombre : email || 'este cliente'
  return { cliente: estado.cliente, nombre, email, expira: estado.expira }
}

/** Cuánto le queda, en milisegundos. Nunca negativo. */
export const msHastaElFin = (visita: Pick<Visita, 'expira'>, ahora: number = Date.now()): number => Math.max(0, visita.expira * 1000 - ahora)

/**
 * Qué hacer cuando una petición dice que no hay sesión y se pregunta al núcleo quién es:
 *
 * - `seguir`: es la misma persona (se volvió a entrar en otra pestaña); se repite lo que falló.
 * - `otra-persona`: en este navegador ha entrado otra; lo que había en pantalla no es suyo.
 * - `visita-terminada`: nadie, y lo que había era una visita del equipo. No se pide contraseña.
 * - `volver-a-entrar`: nadie; «Tu sesión ha caducado», con el correo de quien estaba.
 */
export type AlPerderLaSesion = 'otra-persona' | 'seguir' | 'visita-terminada' | 'volver-a-entrar'

export const alPerderLaSesion = (o: { antes: Pick<Usuario, 'id'>; ahora: null | Pick<Usuario, 'id'>; visita: null | Visita }): AlPerderLaSesion => {
  if (o.ahora) return o.ahora.id === o.antes.id ? 'seguir' : 'otra-persona'
  return o.visita ? 'visita-terminada' : 'volver-a-entrar'
}

/** Donde el equipo vuelve a su panel (el de Payload, en el mismo origen que este). */
export const PANEL_DEL_EQUIPO = '/admin'

/** Lo que dice el diálogo cuando termina una visita: qué ha pasado y por qué, sin pedir nada del cliente. */
export const textosDeVisitaTerminada = (visita: Pick<Visita, 'nombre'>) => ({
  titulo: `La visita a la cuenta de ${visita.nombre} ha terminado`,
  explicacion:
    'Las visitas del equipo a la cuenta de un cliente duran una hora y no se alargan solas. Para seguir aquí, vuelve a entrar en su cuenta desde el panel del equipo.',
  guardar: 'Si vuelves a entrar en otra pestaña, lo que tienes en pantalla sigue aquí: pulsa «Ya he vuelto a entrar» y sigues donde lo dejaste. Si no, lo que no hayas guardado se perderá.',
})
